import React from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';

export const InputSelect = ({
  name,
  label,
  options,
  readOnly = false,
  required = false,
  size = 'medium',
  helperText = '',
  onChange,
  placeholder = 'בחר אפשרות'
}: {
  name: string;
  label?: string;
  options: {
    value: string | number | undefined;
    label: string;
    disabled?: boolean;
    icon?: React.ReactNode;
    tooltip?: string;
  }[];
  readOnly?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
  onChange?: (event: SelectChangeEvent<string | number>) => void;
  placeholder?: string;
}) => {
  const { control } = useFormContext() || {};
  const theme = useTheme();

  return (
    <FormControl fullWidth size={size}>
      {label && <InputLabel required={required}>{label}</InputLabel>}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <>
            <Select
              {...field}
              label={label}
              error={!!error}
              disabled={readOnly}
              displayEmpty
              renderValue={v =>
                !v && !label ? (
                  <span style={{ color: theme.palette.grey[500] }}>{placeholder ?? ''}</span>
                ) : (
                  options.find(o => o.value === v)?.label ?? ''
                )
              }
              onChange={e => {
                field.onChange(e);
                onChange?.(e);
              }}
            >
              {options.map(option => {
                const menuItem = (
                  <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                    {option.icon ? option.icon : <span style={{ width: 24, display: 'inline-block' }} />}
                    {option.label}
                  </MenuItem>
                );

                // Only wrap disabled items with tooltip in a span for tooltip to work
                // Disabled MenuItems don't handle clicks anyway, so wrapping them doesn't block events
                if (option.disabled && option.tooltip) {
                  return (
                    <Tooltip key={option.value} title={option.tooltip} arrow placement="bottom-end">
                      <span style={{ display: 'block', width: '100%' }}>{menuItem}</span>
                    </Tooltip>
                  );
                }

                return menuItem;
              })}
            </Select>
            {(helperText || error?.message) && (
              <FormHelperText sx={{ color: error?.message ? 'error.main' : 'text.secondary' }}>
                {error?.message || helperText}
              </FormHelperText>
            )}
          </>
        )}
        rules={{ required }}
      />
    </FormControl>
  );
};

export default InputSelect;
